import React from 'react';

import { NotionBlock } from '@Models';
import { getYouTubeEmbedUrl, isImageUrl, isVideoUrl, isYouTubeUrl } from './helpers';
import RichText from './RichText';

interface MediaRendererProps {
  block: NotionBlock;
  type: 'image' | 'video' | 'embed';
}

const MediaRenderer = ({ block, type }: MediaRendererProps) => {
  const { url } = block.content;
  const { caption } = block.content;

  if (!url) return null;

  // Render caption if exists
  const renderCaption = () => {
    if (!caption) return null;
    return (
      <p className="text-sm mt-2 text-center max-w-2xl">
        <RichText segments={caption.rich_content || []} defaultColor="text-gray-700" />
      </p>
    );
  };

  // Image rendering
  if (type === 'image' && isImageUrl(url)) {
    return (
      <div className="my-4 flex flex-col items-center justify-center">
        <img src={url} alt="Notion content" className="max-w-full h-auto rounded-lg" />
        {renderCaption()}
      </div>
    );
  }

  // Video rendering
  if (type === 'video') {
    if (isYouTubeUrl(url)) {
      return (
        <div className="my-4">
          <iframe
            src={getYouTubeEmbedUrl(url)}
            title="YouTube video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full rounded-lg h-[400px] border-0"
          />
          {renderCaption()}
        </div>
      );
    }
    if (isVideoUrl(url)) {
      return (
        <div className="my-4">
          <video src={url} controls className="w-full h-auto rounded-lg max-h-[400px]">
            <track kind="captions" srcLang="en" label="English captions" />
          </video>
          {renderCaption()}
        </div>
      );
    }
  }

  // Embed rendering
  if (type === 'embed' && isYouTubeUrl(url)) {
    return (
      <div className="my-4">
        <iframe
          src={getYouTubeEmbedUrl(url)}
          title="Embedded content"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full rounded-lg h-[400px] border-0"
        />
        {renderCaption()}
      </div>
    );
  }

  return null;
};

export default MediaRenderer;
